import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import Breadcrumbs from '../../components/ui/breadcrumbs';
import Button from '../../components/ui/button';
import { Users, Building, FileText, Settings, BarChart3, Mail } from 'lucide-react';
import axios from '../../utils/axios';

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    users: {
      total: 0,
      active: 0,
      last_30_days: 0
    },
    organizations: {
      total: 0,
      active: 0
    },

    posts: {
      total: 0,
      published: 0
    },
    newsletter_subscriptions: {
      total: 0,
      active: 0,
      this_month: 0,
      newsletters_sent: 0
    },
    waitlist: {
      total: 0
    }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get('/admin/dashboard');
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error fetching admin stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!user || !['super_admin', 'admin_user', 'admin_staff', 'admin_super'].includes(user.role)) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <Breadcrumbs />
      </div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
        <p className="text-gray-600">Welcome back, {user.name}. Here's what's happening with your platform.</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card role="button" tabIndex={0} onClick={() => navigate('/admin/users')} onKeyDown={(e) => (e.key==='Enter'||e.key===' ')&&navigate('/admin/users')} className="hover:bg-accent/40 focus-visible:ring-2">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{loading ? '...' : stats.users.total}</div>
            <p className="text-xs text-muted-foreground">Registered users</p>
          </CardContent>
        </Card>

        <Card role="button" tabIndex={0} onClick={() => navigate('/admin/organizations')} onKeyDown={(e) => (e.key==='Enter'||e.key===' ')&&navigate('/admin/organizations')} className="hover:bg-accent/40 focus-visible:ring-2">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Organizations</CardTitle>
            <Building className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{loading ? '...' : stats.organizations.total}</div>
            <p className="text-xs text-muted-foreground">Active organizations</p>
          </CardContent>
        </Card>

        <Card role="button" tabIndex={0} onClick={() => navigate('/admin/llm-requests')} onKeyDown={(e) => (e.key==='Enter'||e.key===' ')&&navigate('/admin/llm-requests')} className="hover:bg-accent/40 focus-visible:ring-2">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Website Checks</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{loading ? '...' : '0'}</div>
            <p className="text-xs text-muted-foreground">Total checks</p>
          </CardContent>
        </Card>

        <Card role="button" tabIndex={0} onClick={() => navigate('/admin/users')} onKeyDown={(e) => (e.key==='Enter'||e.key===' ')&&navigate('/admin/users')} className="hover:bg-accent/40 focus-visible:ring-2">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Users</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{loading ? '...' : stats.users.last_30_days}</div>
            <p className="text-xs text-muted-foreground">Last 30 days</p>
          </CardContent>
        </Card>

        <Card role="button" tabIndex={0} onClick={() => navigate('/admin/blog-posts')} onKeyDown={(e) => (e.key==='Enter'||e.key===' ')&&navigate('/admin/blog-posts')} className="hover:bg-accent/40 focus-visible:ring-2">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Blog Posts</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{loading ? '...' : stats.posts.published}</div>
            <p className="text-xs text-muted-foreground">Published posts</p>
          </CardContent>
        </Card>

        <Card role="button" tabIndex={0} onClick={() => navigate('/admin/newsletter')} onKeyDown={(e) => (e.key==='Enter'||e.key===' ')&&navigate('/admin/newsletter')} className="hover:bg-accent/40 focus-visible:ring-2">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Newsletter</CardTitle>
            <Mail className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-xs text-muted-foreground">Subscribers</div>
                <div className="text-xl font-bold">{loading ? '…' : stats.newsletter_subscriptions.total}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Active</div>
                <div className="text-xl font-bold">{loading ? '…' : stats.newsletter_subscriptions.active}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">This Month</div>
                <div className="text-xl font-bold">{loading ? '…' : stats.newsletter_subscriptions.this_month}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Sent</div>
                <div className="text-xl font-bold">{loading ? '…' : stats.newsletter_subscriptions.newsletters_sent}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card role="button" tabIndex={0} onClick={() => navigate('/admin/waitlist')} onKeyDown={(e) => (e.key==='Enter'||e.key===' ')&&navigate('/admin/waitlist')} className="hover:bg-accent/40 focus-visible:ring-2">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Waitlist</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{loading ? '...' : stats.waitlist.total}</div>
            <p className="text-xs text-muted-foreground">Signups</p>
          </CardContent>
        </Card>

        <Card role="button" tabIndex={0} onClick={() => navigate('/admin/llm-requests')} onKeyDown={(e) => (e.key==='Enter'||e.key===' ')&&navigate('/admin/llm-requests')} className="hover:bg-accent/40 focus-visible:ring-2">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Recent Activity</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{loading ? '...' : stats.users.last_30_days}</div>
            <p className="text-xs text-muted-foreground">New users (30d)</p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              User Management
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">Manage users, roles, and permissions</p>
            <Button className="w-full" onClick={() => window.location.href = '/admin/users'}>
              Manage Users
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building className="h-5 w-5" />
              Organizations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">View and manage organizations</p>
            <Button className="w-full" onClick={() => window.location.href = '/admin/organizations'}>
              Manage Organizations
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              System Settings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">Configure system settings and preferences</p>
            <Button className="w-full" onClick={() => window.location.href = '/admin/settings'}>
              System Settings
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Blog Posts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">Create and manage blog posts</p>
            <Button className="w-full" onClick={() => window.location.href = '/admin/blog-posts'}>
              Manage Posts
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Newsletter
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">Manage newsletter subscribers and campaigns</p>
            <Button className="w-full" onClick={() => window.location.href = '/admin/newsletter'}>
              Manage Newsletter
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Waitlist
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">View and manage waitlist signups</p>
            <Button className="w-full" onClick={() => window.location.href = '/admin/waitlist'}>
              Manage Waitlist
            </Button>
          </CardContent>
        </Card>


      </div>
    </div>
  );
};

export default AdminDashboard;
